"use client";
import React from "react";
import { motion } from "motion/react";
import {
  Award,
  Target,
  Eye,
  Heart,
  Users,
  Factory,
  CheckCircle,
  Globe,
  Leaf,
} from "lucide-react";
import { GlassCard } from "@/src/components/GlassCard";
import { ImageWithFallback } from "@/src/components/figma/ImageWithFallback";
import { AnimatedCounter } from "@/src/components/AnimatedCounter";
import { useTranslation } from "react-i18next";

export const AboutPage: React.FC = () => {
  const { t } = useTranslation("common");

  const values = [
    {
      icon: Leaf,
      title: t("about.values.sustainability.title"),
      description: t("about.values.sustainability.description"),
    },
    {
      icon: Award,
      title: t("about.values.innovation.title"),
      description: t("about.values.innovation.description"),
    },
    {
      icon: CheckCircle,
      title: t("about.values.quality.title"),
      description: t("about.values.quality.description"),
    },
    {
      icon: Users,
      title: t("about.values.customerFocus.title"),
      description: t("about.values.customerFocus.description"),
    },
  ];

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-24">
        <div className="absolute inset-0 z-0">
          <ImageWithFallback
            src="https://images.unsplash.com/photo-1635961179148-3d886568775c?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwcGFja2FnaW5nJTIwZmFjdG9yeSUyMG1vZGVybnxlbnwxfHx8fDE3NzE2MDc5NDV8MA&ixlib=rb-4.1.0&q=80&w=1080"
            alt="About Walnut Packaging"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-walnut-900/90 via-walnut-800/80 to-walnut-600/70 dark:from-black/90 dark:via-walnut-900/85 dark:to-walnut-800/75" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto text-center py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
              {t("about.hero.title")}
            </h1>
            <p className="text-xl text-walnut-100 max-w-3xl mx-auto">
              {t("about.hero.subtitle")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-walnut-900 dark:text-white mb-6">
                {t("about.story.title")}
              </h2>
              <p className="text-lg text-walnut-700 dark:text-walnut-300 mb-4">
                {t("about.story.paragraph1")}
              </p>
              <p className="text-lg text-walnut-700 dark:text-walnut-300 mb-4">
                {t("about.story.paragraph2")}
              </p>
              <p className="text-lg text-walnut-700 dark:text-walnut-300">
                {t("about.story.paragraph3")}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <GlassCard className="p-0 overflow-hidden">
                <ImageWithFallback
                  src="https://images.unsplash.com/photo-1739204618173-3e89def7140f?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb3JydWdhdGVkJTIwY2FyZGJvYXJkJTIwYm94ZXMlMjB3YXJlaG91c2V8ZW58MXx8fHwxNzcxNjA3OTQ1fDA&ixlib=rb-4.1.0&q=80&w=1080"
                  alt="Our facility"
                  className="w-full h-96 object-cover"
                />
              </GlassCard>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            <GlassCard className="p-8">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-walnut-500 to-walnut-700 flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-walnut-900 dark:text-white mb-4">
                {t("about.mission.title")}
              </h2>
              <p className="text-lg text-walnut-700 dark:text-walnut-300">
                {t("about.mission.description")}
              </p>
            </GlassCard>

            <GlassCard className="p-8">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-walnut-500 to-walnut-700 flex items-center justify-center mb-6">
                <Eye className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-walnut-900 dark:text-white mb-4">
                {t("about.vision.title")}
              </h2>
              <p className="text-lg text-walnut-700 dark:text-walnut-300">
                {t("about.vision.description")}
              </p>
            </GlassCard>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-walnut-900 dark:text-white mb-4">
              {t("about.values.title")}
            </h2>
            <p className="text-lg text-walnut-700 dark:text-walnut-300 max-w-2xl mx-auto">
              {t("about.values.subtitle")}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <GlassCard key={index} className="p-6">
                <div className="w-12 h-12 rounded-lg bg-walnut-100 dark:bg-walnut-800 flex items-center justify-center mb-4">
                  <value.icon className="w-6 h-6 text-walnut-700 dark:text-walnut-300" />
                </div>
                <h3 className="text-xl font-semibold text-walnut-900 dark:text-white mb-3">
                  {value.title}
                </h3>
                <p className="text-walnut-700 dark:text-walnut-300">
                  {value.description}
                </p>
              </GlassCard>
            ))}
          </div>
        </div>
      </section>

      {/* Manufacturing Capabilities */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-walnut-900 dark:text-white mb-4">
              {t("about.manufacturing.title")}
            </h2>
            <p className="text-lg text-walnut-700 dark:text-walnut-300 max-w-2xl mx-auto">
              {t("about.manufacturing.subtitle")}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            <GlassCard className="p-8">
              <Factory className="w-12 h-12 text-walnut-700 dark:text-walnut-300 mb-6" />
              <h3 className="text-2xl font-semibold text-walnut-900 dark:text-white mb-6">
                {t("about.manufacturing.capabilitiesTitle")}
              </h3>
              <ul className="space-y-4">
                {(
                  t("about.manufacturing.capabilities", {
                    returnObjects: true,
                  }) as string[]
                ).map((capability: string, index: number) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-walnut-600 dark:text-walnut-400 flex-shrink-0 mt-0.5" />
                    <span className="text-walnut-700 dark:text-walnut-300">
                      {capability}
                    </span>
                  </li>
                ))}
              </ul>
            </GlassCard>

            <GlassCard className="p-8">
              <Award className="w-12 h-12 text-walnut-700 dark:text-walnut-300 mb-6" />
              <h3 className="text-2xl font-semibold text-walnut-900 dark:text-white mb-6">
                {t("about.manufacturing.certificationsTitle")}
              </h3>
              <ul className="space-y-4">
                {(
                  t("about.manufacturing.certifications", {
                    returnObjects: true,
                  }) as string[]
                ).map((cert: string, index: number) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-walnut-600 dark:text-walnut-400 flex-shrink-0 mt-0.5" />
                    <span className="text-walnut-700 dark:text-walnut-300">
                      {cert}
                    </span>
                  </li>
                ))}
              </ul>
            </GlassCard>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-walnut-900 dark:text-white mb-4">
              {t("about.journey.title")}
            </h2>
            <p className="text-lg text-walnut-700 dark:text-walnut-300 max-w-2xl mx-auto">
              {t("about.journey.subtitle")}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(
              t("about.journey.milestones", {
                returnObjects: true,
              }) as { year: string; event: string }[]
            ).map((milestone, index) => (
              <GlassCard key={index} className="p-6">
                <div className="text-3xl font-bold text-walnut-600 dark:text-walnut-400 mb-2">
                  {milestone.year}
                </div>
                <p className="text-lg text-walnut-900 dark:text-white">
                  {milestone.event}
                </p>
              </GlassCard>
            ))}
          </div>
        </div>
      </section>

      {/* Team Overview */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <GlassCard className="p-12 text-center">
            <Users className="w-16 h-16 text-walnut-700 dark:text-walnut-300 mx-auto mb-6" />
            <h2 className="text-4xl font-bold text-walnut-900 dark:text-white mb-6">
              {t("about.team.title")}
            </h2>
            <p className="text-lg text-walnut-700 dark:text-walnut-300 max-w-3xl mx-auto mb-8">
              {t("about.team.description")}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              <div>
                <div className="text-4xl font-bold text-walnut-800 dark:text-walnut-200 mb-2">
                  <AnimatedCounter end={150} suffix="+" />
                </div>
                <div className="text-sm text-walnut-600 dark:text-walnut-400">
                  {t("about.team.stats.members")}
                </div>
              </div>
              <div>
                <div className="text-4xl font-bold text-walnut-800 dark:text-walnut-200 mb-2">
                  <AnimatedCounter end={25} suffix="+" />
                </div>
                <div className="text-sm text-walnut-600 dark:text-walnut-400">
                  {t("about.team.stats.designers")}
                </div>
              </div>
              <div>
                <div className="text-4xl font-bold text-walnut-800 dark:text-walnut-200 mb-2">
                  <AnimatedCounter end={50} suffix="+" />
                </div>
                <div className="text-sm text-walnut-600 dark:text-walnut-400">
                  {t("about.team.stats.engineers")}
                </div>
              </div>
              <div>
                <div className="text-4xl font-bold text-walnut-800 dark:text-walnut-200 mb-2">
                  <AnimatedCounter end={75} suffix="+" />
                </div>
                <div className="text-sm text-walnut-600 dark:text-walnut-400">
                  {t("about.team.stats.production")}
                </div>
              </div>
            </div>
          </GlassCard>
        </div>
      </section>
    </div>
  );
};
